import { Helmet } from "react-helmet";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { isEmpty, omitBy, isNil, map } from "lodash";
import moment from "moment";
import { useQuery } from "Hooks/index";
import useSession from "Hooks/session.hook";
import { getProfileID, toaster } from "Helpers/index";
import "./report-orders.scss";
import { Sorting, SortingMode } from "../../../types/common.types";
import { utils, write } from "xlsx";
import ModuleTitleBar from "Components/shared/module-title-bar/module-title-bar";
import ReportOrderList from "Components/order-management/report-order-list/report-order-list";
import {
  getReportLists,
  getReportOptions,
  SERVICE_URL,
  triggerSseReport,
} from "Services/reports.services";
import { fetchCustomerLocationList } from "Services/location.service";
import usePagination from "Hooks/pagination.hook";
import { toCapitalisedSpaced } from "Helpers/to-capitalised-spaced";
import { getStatusStyle } from "Helpers/get-status-colours";
import useCorporateType from "Hooks/corporate-type.hook";
import { createSessionService } from "Services/session.service";
import { Progress } from "antd";

const reportModel = (data) => {
  let additionalCharges: any = {
    deliveryType: {
      name: null,
      amount: null,
    },
    serviceType: {
      name: null,
      amount: null,
    },
    specialCondition: null,
    specialCare: null,
  };

  for (let i = 0; i < data?.additionalCharges?.length; i++) {
    const charge = data?.additionalCharges[i];

    switch (charge.type) {
      case "DELIVERY_TYPE":
        charge.name
          ? (additionalCharges.deliveryType.name = charge.name)
          : null;
        charge.amount
          ? (additionalCharges.deliveryType.amount = charge.amount)
          : null;
        break;
      case "SERVICE_TYPE":
        charge.name ? (additionalCharges.serviceType.name = charge.name) : null;
        charge.amount
          ? (additionalCharges.serviceType.amount = charge.amount)
          : null;
        break;
      case "SPECIAL_CONDITION":
        charge.amount
          ? (additionalCharges.specialCondition = charge.amount)
          : null;
        break;
      case "SPECIAL_CARE":
        charge.amount ? (additionalCharges.specialCare = charge.amount) : null;
        break;
      case "OTHERS":
        charge.amount ? (additionalCharges.others = charge.amount) : null;
      default:
    }
  }

  return {
    "Order Date": data?.createdBy
      ? moment(data?.createdAt).format("DD-MM-YYYY hh:mm")
      : null,
    "Order Update Date": data?.orderLogCreatedAt
      ? moment(data?.orderLogCreatedAt).format("DD-MM-YYYY hh:mm")
      : null,
    Company: data?.senderID?.companyName,
    Customer: data?.senderID?.name,
    "Customer Location": data?.locationName,
    "Order No": data?.orderCode,
    "Tracking Number": data?.trackingNumber,
    "Delivery Date": data?.deliveryDate
      ? moment(data?.deliveryDate).format("DD-MM-YYYY hh:mm")
      : null,
    "Delivery Type": additionalCharges?.deliveryType.name ?? null,
    "Service Type": additionalCharges?.serviceType.name ?? null,
    Volume: data?.volume,
    Weight: data?.weight,
    "Product Type": data?.parcels[0]?.name,
    "Product Desc": null,
    "Order Status": data?.orderLogStatus
      ? toCapitalisedSpaced(data?.orderLogStatus)
      : null,
    "Base Charges": data?.baseCharges,
    "Special Condition": additionalCharges.specialCondition ?? null,
    "Special Care": additionalCharges.specialCare ?? null,
    "Delivery Type Charges": additionalCharges?.deliveryType.amount ?? null,
    "Service Type Charges": additionalCharges?.serviceType.amount ?? null,
    "Other Charges": additionalCharges.others ?? null,
    "Unit Price": data?.totalCharges,
    "Payment Status": data?.paymentStatus,
    "Failed Delivery Attempt": data?.returnInfo?.failedAttempt || 0,
    "Issue Type": data?.returnInfo?.reason[0].failedDeliveryRemarks,
    Remarks: data?.remarks,
  };
};

const ReportOrders: React.FC<any> = () => {
  const query = useQuery();
  const { t } = useTranslation();
  const { org } = useSession();
  const initialSorting = { sortBy: "orgName", mode: SortingMode.DESC };
  const page: any = query.get("page") || 1;

  const { isCustomer } = useCorporateType();

  const initialFilters = {
    orgID: [org._id],
    sortByCreatedAt: SortingMode.DESC,
  };

  const currentPath = "/parcels-order-processing/reporting";
  const [currentListCount, setCurrentListCount] = useState(0);

  const columns = [
    {
      title: "#",
      dataIndex: "no",
      render: (text: any, record: any, index: number) =>
        (page - 1) * itemPerPage + (index + 1),
    },
    {
      title: t("label.orderDateAndTime").toLocaleUpperCase(),
      dataIndex: "orderDate",
      key: "orderDate",
      ellipsis: true,
      render: (_: any, record: any) => {
        return (
          <span>
            {/* {record?.createdAt} */}
            {moment(record?.createdAt).format("DD-MM-YYYY") +
              " | " +
              moment(record?.createdAt).format("hh:mm a")}
          </span>
        );
      },
    },
    {
      title: t("label.orderUpdateDateAndTime").toLocaleUpperCase(),
      dataIndex: ["orderLogCreatedAt"],
      key: "orderUpdateDate",
      ellipsis: true,
      render: (text: any) => {
        return (
          <span>
            {moment(text).format("DD-MM-YYYY") +
              " | " +
              moment(text).format("hh:mm a")}
          </span>
        );
      },
    },
    {
      title: t("label.company").toLocaleUpperCase(),
      dataIndex: "company",
      key: "company",
      ellipsis: true,
      render: (_: any, record: any) => {
        return <span>{record?.senderID?.companyName}</span>;
      },
    },
    {
      title: t("label.customer").toLocaleUpperCase(),
      dataIndex: "customer",
      key: "customer",
      ellipsis: true,
      render: (_: any, record: any) => {
        return <span>{record?.senderID?.name}</span>;
      },
    },
    {
      title: t("title.customerLocation").toLocaleUpperCase(),
      dataIndex: "customerLocation",
      key: "customerLocation",
      ellipsis: true,
      render: (_: any, record: any) => {
        return <span>{record?.locationName}</span>;
      },
    },
    {
      title: t("label.orderNo").toLocaleUpperCase(),
      dataIndex: "orderID",
      key: "orderID",
      ellipsis: true,
      render: (_: any, record: any) => {
        return <span>{record?.orderCode}</span>;
      },
    },
    {
      title: t("label.trackingNo").toLocaleUpperCase(),
      dataIndex: "trackingID",
      key: "trackingID",
      ellipsis: true,
      render: (_: any, record: any) => {
        return <span>{record?.trackingNumber}</span>;
      },
    },
    {
      title: t("label.deliveryDate").toLocaleUpperCase(),
      dataIndex: "deliveryDate",
      key: "deliveryDate",
      ellipsis: true,
      render: (_: any, record: any) => {
        return <span>{moment(record?.deliveryDate).format("DD-MM-YYYY")}</span>;
      },
    },
    {
      title: t("label.pickUpDateAndTime").toLocaleUpperCase(),
      dataIndex: ["firstMileInfoData", "pickupData", "pickupDate"],
      key: "pickupDate",
      ellipsis: true,
      render: (text: any) =>
        text ? moment(text).format("DD-MM-YYYY hh:mm a") : null,
    },
    {
      title: t("label.deliveryTypes").toLocaleUpperCase(),
      dataIndex: "deliveryTypes",
      key: "deliveryTypes",
      ellipsis: true,
      render: (_: any, record: any) => {
        return !isEmpty(record.additionalCharges) ? (
          <React.Fragment>
            {!isEmpty(
              record.additionalCharges.filter(
                (type) => type.type == "DELIVERY_TYPE",
              ),
            ) ? (
              record.additionalCharges
                .filter((type) => type.type == "DELIVERY_TYPE")
                .map((filteredType) => <span>{filteredType.name}</span>)
            ) : (
              <span>&#11835;</span>
            )}
          </React.Fragment>
        ) : (
          <span>Not calculated due to unsuccessful status</span>
        );
      },
    },
    {
      title: t("label.serviceType").toLocaleUpperCase(),
      dataIndex: "serviceType",
      key: "serviceType",
      ellipsis: true,
      render: (_: any, record: any) => {
        return !isEmpty(record.additionalCharges) ? (
          <React.Fragment>
            {!isEmpty(
              record.additionalCharges.filter(
                (type) => type.type == "SERVICE_TYPE",
              ),
            ) ? (
              record.additionalCharges
                .filter((type) => type.type == "SERVICE_TYPE")
                .map((filteredType) => <span>{filteredType.name}</span>)
            ) : (
              <span>&#11835;</span>
            )}
          </React.Fragment>
        ) : (
          <span>Not calculated due to unsuccessful status</span>
        );
      },
    },
    {
      title: t("label.cubicVolume").toLocaleUpperCase(),
      dataIndex: "volume",
      key: "volume",
      ellipsis: true,
      render: (_: any, record: any) => {
        return <span>{parseInt(record.volume).toLocaleString()}</span>;
      },
    },
    {
      title: t("label.weightInKg").toLocaleUpperCase(),
      dataIndex: "weight",
      key: "weight",
      ellipsis: true,
      render: (_: any, record: any) => {
        return <span>{record?.weight}</span>;
      },
    },
    {
      title: t("label.productType").toLocaleUpperCase(),
      dataIndex: "productType",
      key: "productType",
      ellipsis: true,
      render: (_: any, record: any) => {
        return <span>{record?.parcels[0]?.name}</span>;
      },
    },
    {
      title: t("label.productDesc").toLocaleUpperCase(),
      dataIndex: "description",
      key: "description",
      ellipsis: true,
    },
    {
      title: t("label.orderStatus").toLocaleUpperCase(),
      dataIndex: "orderLogStatus",
      key: "orderStatus",
      ellipsis: true,
      render: (text: any, record: any) => {
        return (
          <span className="status-indicator" style={getStatusStyle(text)}>
            {text ? toCapitalisedSpaced(text) : ""}
          </span>
        );
      },
    },
    {
      title: t("label.baseCharges").toLocaleUpperCase() + " (RM)",
      dataIndex: "baseCharges",
      ellipsis: true,
    },
    {
      title: t("label.specialCondition").toLocaleUpperCase() + " (RM)",
      dataIndex: "specialCondition",
      width: 130,
      ellipsis: true,
      render: (_: any, record: any, rowIndex: string) => {
        return !isEmpty(record.additionalCharges) ? (
          <React.Fragment>
            {!isEmpty(
              record.additionalCharges.filter(
                (type) => type.type == "SPECIAL_CONDITION",
              ),
            ) ? (
              record.additionalCharges
                .filter((type) => type.type == "SPECIAL_CONDITION")
                .map((filteredType) => <span>{filteredType.amount}</span>)
            ) : (
              <span>&#11835;</span>
            )}
          </React.Fragment>
        ) : (
          <span> Not calculated due to unsuccessful status</span>
        );
      },
    },
    {
      title: t("label.specialCare").toLocaleUpperCase() + " (RM)",
      dataIndex: "specialCare",
      width: 130,
      ellipsis: true,
      render: (_: any, record: any, rowIndex: string) => {
        return !isEmpty(record.additionalCharges) ? (
          <React.Fragment>
            {!isEmpty(
              record.additionalCharges.filter(
                (type) => type.type == "SPECIAL_CARE",
              ),
            ) ? (
              record.additionalCharges
                .filter((type) => type.type == "SPECIAL_CARE")
                .map((filteredType) => <span>{filteredType.amount}</span>)
            ) : (
              <span>&#11835;</span>
            )}
          </React.Fragment>
        ) : (
          <span> Not calculated due to unsuccessful status</span>
        );
      },
    },
    {
      title: t("label.deliveryTypes").toLocaleUpperCase() + " (RM)",
      dataIndex: "deliveryTypes",
      width: 130,
      ellipsis: true,
      render: (_: any, record: any, rowIndex: string) => {
        return !isEmpty(record.additionalCharges) ? (
          <React.Fragment>
            {!isEmpty(
              record.additionalCharges.filter(
                (type) => type.type == "DELIVERY_TYPE",
              ),
            ) ? (
              record.additionalCharges
                .filter((type) => type.type == "DELIVERY_TYPE")
                .map((filteredType) => <span>{filteredType.amount}</span>)
            ) : (
              <span>&#11835;</span>
            )}
          </React.Fragment>
        ) : (
          <span>Not calculated due to unsuccessful status</span>
        );
      },
    },
    {
      title: t("label.serviceType").toLocaleUpperCase() + " (RM)",
      dataIndex: "serviceType",
      width: 130,
      ellipsis: true,
      render: (_: any, record: any, rowIndex: string) => {
        return !isEmpty(record.additionalCharges) ? (
          <React.Fragment>
            {!isEmpty(
              record.additionalCharges.filter(
                (type) => type.type == "SERVICE_TYPE",
              ),
            ) ? (
              record.additionalCharges
                .filter((type) => type.type == "SERVICE_TYPE")
                .map((filteredType) => <span>{filteredType.amount}</span>)
            ) : (
              <span>&#11835;</span>
            )}
          </React.Fragment>
        ) : (
          <span>Not calculated due to unsuccessful status</span>
        );
      },
    },
    {
      title: t("label.others").toLocaleUpperCase() + " (RM)",
      dataIndex: "additionalCharges",
      width: 130,
      ellipsis: true,
      render: (_: any, record: any, rowIndex: string) => {
        return !isEmpty(record.additionalCharges) ? (
          <React.Fragment>
            <ul>
              {!isEmpty(
                record.additionalCharges.filter(
                  (type) => type.type == "OTHERS",
                ),
              ) ? (
                record.additionalCharges
                  .filter((type) => type.type == "OTHERS")
                  .map((filteredType) => (
                    <li>
                      {" "}
                      {filteredType.name} <span>&#8594;</span>{" "}
                      {filteredType.amount}
                    </li>
                  ))
              ) : (
                <span>&#11835;</span>
              )}
            </ul>
          </React.Fragment>
        ) : (
          <span>Not calculated due to unsuccessful status</span>
        );
      },
    },
    {
      title: t("label.unitPriceInRM").toLocaleUpperCase(),
      dataIndex: "unitPrice",
      key: "unitPrice",
      ellipsis: true,
      render: (_: any, record: any) => {
        return <span>{record?.totalCharges}</span>;
      },
    },
    {
      title: t("label.paymentStatus").toLocaleUpperCase(),
      dataIndex: "paymentStatus",
      key: "paymentStatus",
      ellipsis: true,
    },
    {
      title: t("label.failedDeliveryAttempts").toLocaleUpperCase(),
      dataIndex: ["returnInfo", "failedAttempt"],
      key: "failedAttempt",
      ellipsis: true,
      render: (text: number) => {
        return <span>{text || 0}</span>;
      },
    },
    {
      title: t("label.issueType").toLocaleUpperCase(),
      dataIndex: ["returnInfo", "reason"],
      key: "reason",
      ellipsis: true,
      render: (text: any[]) => {
        if (text)
          return (
            <span style={{ whiteSpace: "pre-line" }}>
              {text?.map((p) => p.failedDeliveryRemarks || "-").join("\n")}
            </span>
          );
        return "-";
      },
    },
    {
      title: t("label.remarks").toLocaleUpperCase(),
      dataIndex: "remarks",
      key: "remarks",
      ellipsis: true,
      render: (text, record) => (
        <React.Fragment>
          {text && text.trim() !== "" ? <span>{text}</span> : <span>-</span>}
        </React.Fragment>
      ),
    },
  ];

  const [reportListing, setReportListing] = useState<any>([]);
  const [reportOptions, setReportOptions] = useState<any>({});
  const sorting: Sorting = initialSorting;
  const [locationList, setLocationList] = useState([]);
  const [filterObj, setFilterObj] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isExcelLoading, setIsExcelLoading] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(0);
  const sseRef = useRef<EventSource>();

  const { paginationComponent, itemPerPage, queryParams } = usePagination({
    currentListCount,
    currentPath,
    resetPageDeps: filterObj,
  });

  const stringToBuffer = (s: string): ArrayBuffer => {
    const buf = new ArrayBuffer(s.length);
    const view = new Uint8Array(buf);
    for (let i = 0; i < s.length; i++) {
      view[i] = s.charCodeAt(i) & 0xff;
    }
    return buf;
  };

  useEffect(() => {
    handleRefreshList();
    return () => sseRef.current?.close();
  }, []);

  useEffect(() => {
    if (reportListing && filterObj) {
      getReportListing(filterObj);
    }
  }, [queryParams.offset, itemPerPage, filterObj]);

  const handleRefreshList = () => {
    if (!isCustomer) {
      // fetch customer list
      handleGetReport();
    } else {
      // fetch customer location list
      handleFetchCustomerLocationList(org._id);
    }
    setFilterObj({
      ...filterObj,
      orgID: [org._id],
      onlyLatestStatus: isCustomer ? true : null,
    });
  };

  const onFilter = (filterInput) => {
    // setReportListing && setReportListing([]);

    if (isEmpty(filterInput)) {
      setFilterObj(initialFilters);
      return;
    }

    const filterValue = {
      ...filterObj,
      orgID: filterInput?.customersList
        ? filterInput?.customersList
        : filterInput?.orgID == "all"
        ? [org._id]
        : filterInput?.orgID || [org._id],
      orgType: isCustomer ? "CUSTOMER" : null,
      fromDate: filterInput?.dateFrom || null,
      toDate: filterInput?.dateTo || null,
      orderStatus: filterInput?.status || null,
      deliveryTypes:
        filterInput.deliveryTypes == "all" ? null : filterInput.deliveryTypes,
      sortByCreatedAt: filterInput?.sortByCreatedAt || SortingMode.DESC,
      locationID: filterInput?.customerLocation || null,
      onlyLatestStatus: filterInput?.onlyLatestStatus == "true" ? true : null,
      orFields: filterInput?.orFields || null,
    };

    setFilterObj(filterValue);
  };

  const getReportListing = async (
    filterValue,
    enforceLimit = true,
    isWeb = true,
  ) => {
    isWeb
      ? setIsLoading && setIsLoading(true)
      : setIsExcelLoading && setIsExcelLoading(true);
    let filters: any = omitBy(
      { ...filterObj, ...filterValue, sortByCreatedAt: null },
      isNil,
    );

    const responseData = await getReportLists(
      omitBy(filters, isNil),
      enforceLimit
        ? {
            offset: queryParams.offset,
            limit: itemPerPage,
          }
        : null,
      {
        sortBy: "createdAt",
        mode: filterValue?.sortByCreatedAt,
      },
    );

    if (!responseData.error) {
      // if this is for Excel, do not save the data in the state
      if (isWeb) {
        setReportListing([]);
        setReportListing(responseData.report);
        setCurrentListCount(responseData.report.length);
      } else {
        setIsExcelLoading && setIsExcelLoading(false);
        return responseData.report;
      }
    } else {
      if (typeof responseData.error?.message === "string") {
        toaster("error", responseData.error.message);
      } else {
        toaster("error", "Error fetching order reports.");
      }
    }

    setIsLoading && setIsLoading(false);
    setIsExcelLoading && setIsExcelLoading(false);
  };

  const handleGetReport = async () => {
    setIsLoading && setIsLoading(true);
    const responseData = await getReportOptions(org._id);

    if (responseData && !responseData.error) {
      setReportOptions(responseData);
    } else {
      if (typeof responseData.error?.message === "string") {
        toaster("error", responseData.error.message);
      } else {
        toaster("error", responseData.error.message.message.replace('"', ""));
      }
    }

    setIsLoading && setIsLoading(false);
  };

  const handleFetchCustomerLocationList = async (customerID) => {
    let locationFilters = { tempDelete: false };

    const responseData = await fetchCustomerLocationList(
      locationFilters,
      org._id,
      {
        offset: queryParams.offset,
        limit: itemPerPage,
      },
      sorting,
    );

    if (responseData && !responseData.error) {
      let customerLocations: any = [];
      customerLocations = map(responseData, (resObj) => {
        resObj.locations = resObj;
        return resObj;
      });

      setLocationList(customerLocations);
    } else {
      toaster("error", responseData.error.message);
    }
  };

  const handleDownloadExcelFile = useCallback(() => {
    (async () => {
      setIsExcelLoading(true);
      const profileID = getProfileID();

      const filename = `${new Date().getTime()}.xlsx`;
      const workbook = utils.book_new();
      const worksheet = utils.aoa_to_sheet([[]]);
      utils.book_append_sheet(workbook, worksheet, "Sheet1");

      const SSE_OTP = await createSessionService();
      if (!SSE_OTP) throw "get opt failed";

      sseRef.current = new EventSource(
        `${SERVICE_URL.SSE.SUBSCRIBE}?profileID=${profileID}&ticket=${SSE_OTP}`,
      );

      sseRef.current.onerror = (error) => {
        setTimeout(() => {
          setIsExcelLoading(false);
          setProgress(0);
        }, 1500);
        console.log("SSE connection aborted.", error);
        throw error;
      };

      sseRef.current.onopen = () => {
        console.log("SSE connection established.");
      };

      sseRef.current.addEventListener("SSE_ORDER_REPORT", (event) => {
        let isHeadersSet = false;
        const jsonResponse = JSON.parse(event.data);
        const { data, progress: percentage } = jsonResponse;
        const newData = data.map((item) => reportModel(item));

        if (newData.length > 0) {
          if (!isHeadersSet) {
            const headers = Object.keys(newData[0]);
            utils.sheet_add_aoa(worksheet, [headers], { origin: "A1" });
            isHeadersSet = true;
          }
          utils.sheet_add_json(worksheet, newData, {
            origin: -1,
            skipHeader: true,
          });
        }

        setProgress(percentage.toFixed(0));

        if (percentage === 100) {
          const excelBinary = write(workbook, {
            bookType: "xlsx",
            type: "binary",
          });

          const blob = new Blob([stringToBuffer(excelBinary)], {
            type: "application/octet-stream",
          });

          const downloadLink = document.createElement("a");
          downloadLink.href = URL.createObjectURL(blob);
          downloadLink.download = filename;
          downloadLink.click();

          setTimeout(() => {
            setIsExcelLoading(false);
            setProgress(0);
          }, 1500);
        }
      });

      await triggerSseReport(
        {
          ...filterObj,
          orgIDs: filterObj.orgID,
          profileID: profileID,
        },
        { sortBy: "createdAt", mode: "DESC" },
      );

      if (progress === 100) {
        sseRef.current.close();
      }
    })();
  }, [setIsExcelLoading, triggerSseReport, filterObj]);

  return (
    <div className="report-orders">
      {isExcelLoading && (
        <Progress className="floating-progress-container" percent={progress} />
      )}
      <Helmet>
        <title>{t("pageTitle.orderProcessing")}</title>
      </Helmet>
      <ModuleTitleBar
        titleText={t("title.orderProcessing")}
        haveSubtitle
        subtitleText={t("title.reporting")}
      />
      <ReportOrderList
        columns={columns}
        reportOptions={reportOptions}
        onFilter={onFilter}
        reportListing={reportListing}
        onDownloadExcelFile={handleDownloadExcelFile}
        locationList={locationList}
        isLoading={isLoading}
        handleRefreshList={handleRefreshList}
        isExcelLoading={isExcelLoading}
        disableShowLatestStatus={isCustomer}
      />
      <div className="my-30">{paginationComponent}</div>
    </div>
  );
};

export default ReportOrders;
